import { DEFAULT_AUTH_FORMS } from '../store'

export const CLEAR_REDIRECT_URL = 'CLEAR_REDIRECT_URL'
export const SET_REDIRECT_URL = 'SET_REDIRECT_URL'
export const AUTH_UPDATE_FORM_VALUE = 'AUTH_UPDATE_FORM_VALUE'
export const SET_FORM_PENDING = 'SET_FORM_PENDING'
export const SET_FORM_FAILURE = 'SET_FORM_FAILURE'
export const SET_FORM_SUCCESS = 'SET_FORM_SUCCESS'
export const CLEAR_FORM_SUCCESS_AND_ERROR = 'CLEAR_FORM_SUCCESS_AND_ERROR'
export const CLEAR_FORM_DATA = 'CLEAR_FORM_DATA'
export const SET_EMAIL_CHECK_RESPONSE_ID = 'SET_EMAIL_CHECK_RESPONSE_ID'
export const GOTO_REGISTRATION_CURRENT_STEP = 'GOTO_REGISTRATION_CURRENT_STEP'
export const LOAD_LAST_LOGGED_USER_DATA = 'LOAD_LAST_LOGGED_USER_DATA'
export const TOO_MANY_ATTEMPTS = 'TOO_MANY_ATTEMPTS'

/**
 * Set redirect URL
 * @param {Object} state
 * @param {string|null} redirectUrl
 * @returns {Object}
 */
function setRedirectUrl(state, redirectUrl) {
    return {
        ...state,
        redirectUrl,
    }
}

/**
 * Update input field value for certain form
 * @param {Object} state
 * @param {string} formType
 * @param {string} formField
 * @param {string} value
 * @returns {Object}
 */
function updateFormValue(state, formType, formField, value) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            data: {
                ...state[formType].data,
                [formField]: value,
            },
        },
    }
}

/**
 * Set pending state for specific form
 * @param {Object} state
 * @param {Object} formType
 * @returns {Object}
 */
function setFormPending(state, formType) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            isPending: true,
        },
    }
}

/**
 * Set errors for specific form
 * @param {Object} state
 * @param {string} formType
 * @param {Object} errors
 * @returns {Object}
 */
function setFormFailure(state, formType, errors) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            isPending: false,
            errors: errors,
        },
    }
}

/**
 * Set success response data for specific form
 * @param {Object} state
 * @param {string} formType
 * @param {Object} responseData
 * @returns {Object}
 */
function setFormSuccess(state, formType, responseData) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            isPending: false,
            responseData: responseData,
        },
    }
}

/**
 * Clear errors and success data in redux auth form by type
 * @param {Object} state
 * @param {string} formType
 * @returns {{lastLoggedUserForm, loginForm, forgottenPasswordForm, signupForm}}
 */
function clearFormSuccessAndError(state, formType) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            errors: null,
            responseData: null,
        },
    }
}

/**
 * Clear redux auth forms data
 * @returns {{lastLoggedUserForm, loginForm, forgottenPasswordForm, signupForm}}
 */
function clearFormData() {
    return { ...DEFAULT_AUTH_FORMS }
}

/**
 * Save response id after check email exists into state
 * @param {Object} state
 * @param {string} formType
 * @param {number|null} responseId
 * @returns {{lastLoggedUserForm, loginForm, forgottenPasswordForm, signupForm}}
 */
function setCheckEmailResponseId(state, formType, responseId) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            emailCheckResponseId: responseId,
        },
    }
}

/**
 * Change current registration step in response signup form
 * @param {Object} state
 * @param {string} formType
 * @param {number} step
 * @returns {{lastLoggedUserForm, loginForm, forgottenPasswordForm, signupForm}}
 */
function gotoRegistrationCurrentStep(state, formType, step) {
    return {
        ...state,
        [formType]: {
            ...state[formType],
            responsiveCurrentStep: step,
        },
    }
}

function loadLastLoggedUserData(state) {
    const localStorageData = localStorage.getItem('loggedUser')
    const data = localStorageData ? JSON.parse(localStorageData) : null
    return {
        ...state,
        lastLoggedUserForm: {
            data: {
                name: data?.user?.name,
                surname: data?.user?.surname,
                email: data?.user?.email,
                avatar: data?.user?.avatar,
                password: '',
            },
            loggedWith: data?.loggedWith,
        },
    }
}

function toggleErrorTooManyAttempts(state) {
    return {
        ...state,
        loginForm: {
            ...state.loginForm,
            errorTooManyInvalidCredentials: !state.loginForm.errorTooManyInvalidCredentials,
        },
    }
}

/**
 * Reducer for auth forms
 * @param {Object} state
 * @param {Object} action
 * @returns {(function(*): (Promise<any> | any | Observable<any> | Thenable<any> | Promise<any> | undefined))|{lastLoggedUserForm, loginForm, forgottenPasswordForm, signupForm}|{}|{lastLoggedUserForm: {data: {password: string, surname: string, name: string, avatar: string, email: string}, responseData: null, isPending: boolean, errors: null}, loginForm: {data: {password: string, email: string}, responseData: null, isPending: boolean, errors: null}, forgottenPasswordForm: {data: {email: string}, responseData: null, isPending: boolean, errors: null}, signupForm: {data: {password: string, surname: string, name: string, email: string}, responseData: null, isPending: boolean, errors: null}}}
 */
export default function reducer(state = [], action) {
    switch(action.type) {
        case CLEAR_REDIRECT_URL: return setRedirectUrl(state, null)
        case SET_REDIRECT_URL: return setRedirectUrl(state, action.redirectUrl)
        case AUTH_UPDATE_FORM_VALUE: return updateFormValue(state, action.formType, action.formField, action.value)
        case SET_FORM_PENDING: return setFormPending(state, action.formType)
        case SET_FORM_FAILURE: return setFormFailure(state, action.formType, action.errors)
        case SET_FORM_SUCCESS: return setFormSuccess(state, action.formType, action.responseData)
        case CLEAR_FORM_SUCCESS_AND_ERROR: return clearFormSuccessAndError(state, action.formType)
        case CLEAR_FORM_DATA: return clearFormData()
        case SET_EMAIL_CHECK_RESPONSE_ID: return setCheckEmailResponseId(state, action.formType, action.responseId)
        case GOTO_REGISTRATION_CURRENT_STEP: return gotoRegistrationCurrentStep(state, action.formType, action.step)
        case LOAD_LAST_LOGGED_USER_DATA: return loadLastLoggedUserData(state)
        case TOO_MANY_ATTEMPTS: return toggleErrorTooManyAttempts(state)
        default: return state
    }
}
